import { lazy, Suspense, useEffect, useRef, useState, type ComponentType } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Redirect, Route, Switch, useLocation } from 'wouter';
import { CheckCircle2, Undo2, XCircle } from 'lucide-react';
import { datKhiHetPhien } from './lib/api';
import { NhaBoLoc, useNgan, useViecDangMo } from './lib/bo-loc';
import { ghiCoPhien, hoanTacGanNhat, KHOA, useToi } from './lib/du-lieu';
import { useThongBao } from './lib/thong-bao';
import { cx } from './components/ui';
import { TrangDangNhap } from './views/DangNhap';

// Mỗi trang một chunk — lần đầu chỉ tải khung + trang đang mở. Ngăn chi tiết kéo theo marked +
// DOMPurify, Kanban kéo theo dnd-kit.
//
// Chunk ĐÃ VỀ lúc render → dùng thẳng component, không qua Suspense. Lý do: sau khi hiện fallback, React giữ nội dung
// thêm tới 300 ms (FALLBACK_THROTTLE_MS) dù chunk đã về. Đo thật 28/09: chunk về lúc 129 ms, nhưng trang và mọi lượt
// tải dữ liệu của nó chỉ bắt đầu lúc 429 ms. Chunk của trang đang mở được tải trước ngay khi mở app (dưới đây); các
// trang còn lại tải lúc rảnh, nên chuyển trang cũng không phải chờ.
function trangNhanh(nap: () => Promise<ComponentType>) {
  let da_co: ComponentType | null = null;
  let hua: Promise<ComponentType> | null = null;
  const tai = () => (hua ??= nap().then((c) => (da_co = c), (e) => { hua = null; throw e; }));
  const Cham = lazy(() => tai().then((c) => ({ default: c })));
  function Trang(props: object) {
    const C = da_co ?? Cham;
    return <C {...props} />;
  }
  return Object.assign(Trang, { tai: () => tai().catch(() => null) });
}
const TrangTongQuan = trangNhanh(() => import('./views/TongQuan').then((m) => m.TrangTongQuan));
const TrangCuaToi = trangNhanh(() => import('./views/DanhSach').then((m) => m.TrangCuaToi));
const TrangCanDuyet = trangNhanh(() => import('./views/DanhSach').then((m) => m.TrangCanDuyet));
const TrangDanhSach = trangNhanh(() => import('./views/DanhSach').then((m) => m.TrangDanhSach));
const TrangBang = trangNhanh(() => import('./views/Bang').then((m) => m.TrangBang));
const TrangOkr = trangNhanh(() => import('./views/Okr').then((m) => m.TrangOkr));
const TrangKpi = trangNhanh(() => import('./views/Kpi').then((m) => m.TrangKpi));
const TrangHieuSuat = trangNhanh(() => import('./views/HieuSuat').then((m) => m.TrangHieuSuat));
const TrangDoiTac = trangNhanh(() => import('./views/DoiTac').then((m) => m.TrangDoiTac));
const TrangLich = trangNhanh(() => import('./views/Lich').then((m) => m.TrangLich));
const TrangTimeline = trangNhanh(() => import('./views/Timeline').then((m) => m.TrangTimeline));
const TrangThanhVien = trangNhanh(() => import('./views/ThanhVien').then((m) => m.TrangThanhVien));
const TrangCaiDat = trangNhanh(() => import('./views/CaiDat').then((m) => m.TrangCaiDat));
const TrangNhatKy = trangNhanh(() => import('./views/NhatKy').then((m) => m.TrangNhatKy));
const TrangTheoBrand = trangNhanh(() => import('./views/TheoBrand').then((m) => m.TrangTheoBrand));
const TrangTaiSan = trangNhanh(() => import('./views/TaiSan').then((m) => m.TrangTaiSan));
const TrangTaiLieu = trangNhanh(() => import('./views/TaiLieu').then((m) => m.TrangTaiLieu));
const TrangTepBrand = trangNhanh(() => import('./views/TepBrand').then((m) => m.TrangTepBrand));
const TrangThongKe = trangNhanh(() => import('./views/ThongKe').then((m) => m.TrangThongKe));
const TrangThongKeToanCanh = trangNhanh(() => import('./views/ThongKeToanCanh').then((m) => m.TrangThongKeToanCanh));
const TrangThongKeTimKiem = trangNhanh(() => import('./views/ThongKeTimKiem').then((m) => m.TrangThongKeTimKiem));
const TrangMauBangDieuKhien = trangNhanh(() => import('./views/MauBangDieuKhien').then((m) => m.TrangMauBangDieuKhien));
const TrangBaoCaoKy = trangNhanh(() => import('./views/BaoCaoKy').then((m) => m.TrangBaoCaoKy));
const TrangNhanSu = trangNhanh(() => import('./views/NhanSu').then((m) => m.TrangNhanSu));
const TrangKenh = trangNhanh(() => import('./views/Kenh').then((m) => m.TrangKenh));
const TrangDanhBa = trangNhanh(() => import('./views/DanhBa').then((m) => m.TrangDanhBa));
const TrangDoiThu = trangNhanh(() => import('./views/TheoDoi').then((m) => m.TrangDoiThu));
const TrangThiTruong = trangNhanh(() => import('./views/TheoDoi').then((m) => m.TrangThiTruong));
const TrangHeThong = trangNhanh(() => import('./views/HeThong').then((m) => m.TrangHeThong));
const TrangSoDo = trangNhanh(() => import('./views/SoDo').then((m) => m.TrangSoDo));
const TrangVclaw = trangNhanh(() => import('./views/VClaw').then((m) => m.TrangVclaw));
const ChongNgan = trangNhanh(() => import('./components/ChongNgan').then((m) => m.ChongNgan));

const THEO_DUONG: Record<string, { tai: () => Promise<unknown> }> = {
  '/': TrangTongQuan, '/cua-toi': TrangCuaToi, '/can-duyet': TrangCanDuyet, '/danh-sach': TrangDanhSach,
  '/bang': TrangBang, '/theo-brand': TrangTheoBrand, '/lich': TrangLich, '/timeline': TrangTimeline, '/okr': TrangOkr, '/muc-tieu': TrangOkr,
  '/kpi': TrangKpi, '/thong-ke': TrangThongKe, '/thong-ke/toan-canh': TrangThongKeToanCanh, '/thong-ke/tim-kiem': TrangThongKeTimKiem, '/bao-cao': TrangBaoCaoKy, '/mau-bang-dieu-khien': TrangMauBangDieuKhien,'/hieu-suat': TrangHieuSuat, '/doi-tac': TrangDoiTac, '/thanh-vien': TrangThanhVien,
  '/cai-dat': TrangCaiDat, '/nhat-ky': TrangNhatKy, '/tai-san': TrangTaiSan, '/he-thong': TrangHeThong, '/tai-lieu': TrangTaiLieu, '/tep-brand': TrangTepBrand, '/nhan-su': TrangNhanSu, '/danh-ba': TrangDanhBa, '/kenh': TrangKenh, '/doi-thu': TrangDoiThu, '/thi-truong': TrangThiTruong, '/so-do': TrangSoDo, '/vclaw': TrangVclaw,
};
// Tải trước NGAY khi mở app, song song với /api/toi.
const CHUNK_DAU = Promise.all([
  (THEO_DUONG[location.pathname] ?? TrangTongQuan).tai(),
  /[?&](viec|ngan)=/.test(location.search) ? ChongNgan.tai() : null,
]);

function Cho() {
  return <div className="flex h-full items-center justify-center text-[13px] text-chu-mo">Đang tải…</div>;
}

// Chồng ngăn chỉ mount khi có ?ngan= / ?viec= (xem lib/ngan.ts). Chunk của nó (ngăn việc kéo marked + DOMPurify,
// ~31 KB gzip) nhờ vậy không tranh băng thông với dữ liệu lúc mở app; tải lúc rảnh ở dưới.
function NganNeuMo() {
  const { ds } = useNgan();
  return ds.length ? <Suspense fallback={null}><ChongNgan /></Suspense> : null;
}

// Link "#12" trong markdown (href="?viec=12") → mở việc đó thành một ngăn CHỒNG LÊN chồng hiện tại, không tải lại
// trang, không mất trang / ngăn bên dưới.
function BatLinkViec() {
  const [, moViec] = useViecDangMo();
  const mo = useRef(moViec);
  mo.current = moViec;
  const [, dieuHuong] = useLocation();
  const di = useRef(dieuHuong);
  di.current = dieuHuong;
  useEffect(() => {
    const bat = (e: MouseEvent) => {
      const a = (e.target as HTMLElement).closest('a');
      const href = a?.getAttribute('href');
      const so = href?.match(/^\?viec=(\d+)$/)?.[1];
      if (so && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
        e.preventDefault();
        mo.current(Number(so));
        return;
      }
      // "/tai-lieu?brand=…&tep=…" (đường dẫn tệp hub trong nội dung việc) → chuyển trang không tải lại.
      if (href && /^\/tai-lieu\?/.test(href) && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
        e.preventDefault();
        di.current(href);
      }
    };
    document.addEventListener('click', bat);
    return () => document.removeEventListener('click', bat);
  }, []);
  return null;
}

export function App() {
  const toi = useToi();
  const qc = useQueryClient();

  useEffect(() => datKhiHetPhien(() => qc.setQueryData(KHOA.toi, null)), [qc]);

  // Cờ cho napTruoc lần mở sau + tải chồng ngăn và các trang còn lại lúc rảnh (mở việc / chuyển trang khỏi chờ).
  const da_dang_nhap = toi.isSuccess ? !!toi.data : null;
  useEffect(() => {
    if (da_dang_nhap === null) return;
    ghiCoPhien(da_dang_nhap);
    if (!da_dang_nhap) return;
    const id = setTimeout(() => [ChongNgan, ...Object.values(THEO_DUONG)].forEach((t) => void t.tai()), 1000);
    return () => clearTimeout(id);
  }, [da_dang_nhap]);

  // /api/toi về trước chunk của trang → giữ màn "Đang tải…" (KHÔNG phải fallback của Suspense) thêm chút cho chunk
  // về, rồi vẽ thẳng. Vẽ ngay thì Suspense hiện fallback và React giữ trang thêm 300 ms (đo thật ở /hieu-suat).
  const [chunk_san, datChunkSan] = useState(false);
  useEffect(() => {
    let con = true;
    void CHUNK_DAU.then(() => con && datChunkSan(true));
    return () => { con = false; };
  }, []);

  let noi_dung;
  if (toi.isLoading || (toi.data && !chunk_san)) {
    noi_dung = <div className="flex h-full items-center justify-center text-chu-mo">Đang tải…</div>;
  } else if (toi.error) {
    noi_dung = <div className="flex h-full items-center justify-center p-6 text-center text-chu-phu">Không kết nối được máy chủ VTask. Tải lại trang sau ít phút.</div>;
  } else if (!toi.data) {
    noi_dung = <TrangDangNhap />;
  } else {
    noi_dung = (
      <NhaBoLoc>
        <Suspense fallback={<Cho />}>
        <Switch>
          <Route path="/" component={TrangTongQuan} />
          <Route path="/cua-toi" component={TrangCuaToi} />
          <Route path="/can-duyet" component={TrangCanDuyet} />
          <Route path="/danh-sach" component={TrangDanhSach} />
          <Route path="/bang" component={TrangBang} />
          <Route path="/theo-brand" component={TrangTheoBrand} />
          <Route path="/tai-san" component={TrangTaiSan} />
          <Route path="/tai-lieu" component={TrangTaiLieu} />
          <Route path="/tep-brand" component={TrangTepBrand} />
          <Route path="/nhan-su" component={TrangNhanSu} />
          <Route path="/danh-ba" component={TrangDanhBa} />
          <Route path="/kenh" component={TrangKenh} />
          <Route path="/doi-thu" component={TrangDoiThu} />
          <Route path="/thi-truong" component={TrangThiTruong} />
          <Route path="/so-do" component={TrangSoDo} />
          <Route path="/he-thong" component={TrangHeThong} />
          <Route path="/vclaw" component={TrangVclaw} />
          <Route path="/lich" component={TrangLich} />
          <Route path="/timeline" component={TrangTimeline} />
          <Route path="/okr" component={TrangOkr} />
          <Route path="/muc-tieu"><Redirect to="/okr" replace /></Route>
          <Route path="/kpi" component={TrangKpi} />
          <Route path="/thong-ke" component={TrangThongKe} />
          <Route path="/thong-ke/toan-canh" component={TrangThongKeToanCanh} />
          <Route path="/thong-ke/tim-kiem" component={TrangThongKeTimKiem} />
          <Route path="/bao-cao" component={TrangBaoCaoKy} />
          <Route path="/mau-bang-dieu-khien" component={TrangMauBangDieuKhien} />
          <Route path="/hieu-suat" component={TrangHieuSuat} />
          <Route path="/doi-tac" component={TrangDoiTac} />
          <Route path="/thanh-vien" component={TrangThanhVien} />
          <Route path="/cai-dat" component={TrangCaiDat} />
          <Route path="/nhat-ky" component={TrangNhatKy} />
          <Route><TrangTongQuan /></Route>
        </Switch>
        </Suspense>
        <NganNeuMo />
        <BatLinkViec />
      </NhaBoLoc>
    );
  }

  return (
    <>
      {noi_dung}
      <KhayThongBao />
    </>
  );
}

function KhayThongBao() {
  const ds = useThongBao();
  // Ctrl+Z / ⌘Z = hoàn tác thao tác gần nhất còn hạn (lib/hoan-tac.ts). Đang gõ trong ô nhập thì để trình duyệt hoàn tác chữ.
  useEffect(() => {
    const phim = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.repeat || e.shiftKey || e.altKey || !(e.ctrlKey || e.metaKey) || e.key.toLowerCase() !== 'z') return;
      const dang_go = e.target instanceof Element && !!e.target.closest('input,textarea,select,[contenteditable]');
      if (dang_go) return;
      e.preventDefault();
      hoanTacGanNhat();
    };
    document.addEventListener('keydown', phim);
    return () => document.removeEventListener('keydown', phim);
  }, []);
  return (
    <div className="pointer-events-none fixed right-4 bottom-[calc(4.5rem+env(safe-area-inset-bottom))] left-4 z-[60] flex flex-col items-end gap-2 sm:left-auto lg:bottom-4" aria-live="polite">
      {ds.map((m) => (
        <div key={m.id} className={cx('hien pointer-events-auto flex max-w-sm items-start gap-2 rounded-lg border bg-mat px-3 py-2.5 text-[13px] shadow-noi', m.kieu === 'loi' ? 'border-do/40' : 'border-vien')}>
          {m.kieu === 'loi' ? <XCircle size={16} className="mt-0.5 shrink-0 text-do" /> : <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-luc" />}
          <span className="min-w-0 flex-1">{m.noi_dung}</span>
          {m.nut && (
            <button
              type="button"
              className="-my-1 ml-1 inline-flex h-8 shrink-0 items-center gap-1 rounded-md px-2 font-semibold text-xanh hover:bg-mat-2 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-cam"
              aria-label={m.nut.mo_ta || m.nut.nhan}
              title={`${m.nut.mo_ta || m.nut.nhan} (Ctrl+Z)`}
              onClick={m.nut.lam}
            >
              <Undo2 size={14} aria-hidden /> {m.nut.nhan}
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
